import { MOCK_MODE, mockApi } from './mockApi';
import { getOrCreateIdempotencyKey } from './utils/idempotencyKey';

const API_URL = process.env.REACT_APP_TAKEOVER_API_URL || 'http://localhost:8000';

// PR #405 (2026-05-19) — defense-in-depth against the
// idempotency-rejoin token cascade documented in u0lnx + h95ej.
//
// Repro: operator opens a second tab → POST /sessions reuses the same
// localStorage idempotencyKey → BE returns `rejoined: true` with a
// NEW sessionToken (PR #404 BE-side fix keeps prior token-hash valid
// too, but this FE patch makes the cascade impossible REGARDLESS of
// BE behavior). Without this guard, the original tab's polling fires
// with the stale token → 401 → state-poller clears the session →
// next mutation builds /sessions/null/location → 422 cascade.
//
// Behavior 1: persist the LATEST sessionToken on EVERY successful
// createSession response (including rejoined=true). Behavior 3: guard
// every URL-builder against a null/undefined sessionId so we never
// emit a literal `/sessions/null/...` request.
function assertSessionId(sessionId, fnName) {
  if (sessionId === null || sessionId === undefined || sessionId === '' || sessionId === 'null' || sessionId === 'undefined') {
    const err = new Error(`api.${fnName}: sessionId is required (got ${String(sessionId)})`);
    err.code = 'MISSING_SESSION_ID';
    throw err;
  }
}

// Read/write helpers for the seller's session credentials. We keep
// the SAME storage convention the pre-PR-405 code already uses —
// sessionId in localStorage (survives tab close), sessionToken in
// sessionStorage (per-tab) — so existing call-sites keep working.
// `lastCreateAt` (epoch ms) lets App.jsx's 401 grace-window distinguish
// "fresh create, BE just hasn't acked the new token yet" from "session
// genuinely invalidated, clear it".
const STORAGE_KEYS = {
  sessionId: 'takeover.sessionId',
  sessionToken: 'sessionToken',
  lastCreateAt: 'takeover.lastCreateAt',
};

function persistSessionCredentials({ sessionId, sessionToken }) {
  if (typeof window === 'undefined') return;
  try {
    if (sessionId) window.localStorage.setItem(STORAGE_KEYS.sessionId, sessionId);
    if (sessionToken) window.sessionStorage.setItem(STORAGE_KEYS.sessionToken, sessionToken);
    window.sessionStorage.setItem(STORAGE_KEYS.lastCreateAt, String(Date.now()));
  } catch {
    /* private-mode Safari / quota exceeded — non-fatal */
  }
}

export function getLastCreateAt() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEYS.lastCreateAt);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export const __STORAGE_KEYS = STORAGE_KEYS;
export const __assertSessionId = assertSessionId;

// iter-34: FastAPI returns 422s with body.detail as an array of
// validation-error objects: [{loc:[...], msg:'...', type:'...'}, ...].
// Older code passed body.detail straight to new Error(), which called
// toString() on the array → "[object Object],[object Object]". Result
// was operator-visible "[object Object]" errors on Consent and Final
// Details even though the underlying takeover succeeded. Format the
// detail array into a readable string before raising.
function extractErrorMessage(body, statusText) {
  if (!body || typeof body !== 'object') return statusText || 'Request failed.';
  const { detail, error } = body;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e) => (e && typeof e === 'object' ? (e.msg || JSON.stringify(e)) : String(e)))
      .join(', ');
  }
  if (error && typeof error === 'object' && error.message) return error.message;
  if (typeof body.message === 'string') return body.message;
  return statusText || 'Request failed.';
}

async function request(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const token = sessionStorage.getItem('sessionToken');
  if (token) headers['X-Session-Token'] = token;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(extractErrorMessage(body, res.statusText));
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

const realApi = {
  // Referral program (migration 095): best-effort click beacon. Public, no
  // session needed; callers .catch() so a referral hiccup never blocks the page.
  referralClick: (code) => request('/api/takeover/referral/click', { method: 'POST', body: JSON.stringify({ code }) }),
  // PR 4 — createSession now auto-injects the persisted idempotency key
  // so the BE-side dedup window (PR #398) maps refresh / double-click
  // to the same `sessionId`. The BE returns `rejoined: true` when the
  // dedup-cache hit; the Welcome step honours that flag to resume the
  // existing session instead of resetting state.
  //
  // PR #405 (2026-05-19) — defense-in-depth behavior #1: persist the
  // LATEST sessionToken on EVERY successful response (including
  // rejoined=true). Multi-tab repro: tab A creates → token T_a; tab B
  // rejoins with same idempotencyKey → BE returns token T_b. Without
  // this write, whichever tab does the rejoin keeps using T_a while
  // BE has already rotated to T_b → 401 cascade. Writing on every
  // response makes the LAST createSession win, which matches operator
  // intent (the active tab is whichever just created).
  createSession: async (data) => {
    const body = await request('/api/takeover/sessions', {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey: getOrCreateIdempotencyKey(), ...data }),
    });
    // Persist on success, including rejoined=true. The token in the
    // body is authoritative — overwrite any cached value.
    if (body && body.sessionToken) {
      persistSessionCredentials({
        sessionId: body.sessionId,
        sessionToken: body.sessionToken,
      });
    }
    return body;
  },
  updateContact: (id, data) => {
    assertSessionId(id, 'updateContact');
    return request(`/api/takeover/sessions/${id}/contact`, { method: 'PUT', body: JSON.stringify(data) });
  },
  updateCredentials: (id, data) => {
    assertSessionId(id, 'updateCredentials');
    return request(`/api/takeover/sessions/${id}/credentials`, { method: 'PUT', body: JSON.stringify(data) });
  },
  startLogin: (id) => {
    assertSessionId(id, 'startLogin');
    return request(`/api/takeover/sessions/${id}/login`, { method: 'POST' });
  },
  // B-CREDS-RECOVERY (2026-05-29): seller taps "Reset password" on the
  // wrong-credentials overlay → drives LinkedIn forgot-password recovery.
  recoverPassword: (id) => {
    assertSessionId(id, 'recoverPassword');
    return request(`/api/takeover/sessions/${id}/recover-password`, { method: 'POST' });
  },
  submit2FA: (id, data) => {
    assertSessionId(id, 'submit2FA');
    return request(`/api/takeover/sessions/${id}/2fa`, { method: 'PUT', body: JSON.stringify(data) });
  },
  resend2FA: (id) => {
    assertSessionId(id, 'resend2FA');
    return request(`/api/takeover/sessions/${id}/2fa/resend`, { method: 'POST' });
  },
  getProfile: (id) => {
    assertSessionId(id, 'getProfile');
    return request(`/api/takeover/sessions/${id}/profile`);
  },
  updateIDBadge: (id, data) => {
    assertSessionId(id, 'updateIDBadge');
    return request(`/api/takeover/sessions/${id}/id-badge`, { method: 'PUT', body: JSON.stringify(data) });
  },
  startEmailChange: (id) => {
    assertSessionId(id, 'startEmailChange');
    return request(`/api/takeover/sessions/${id}/email-change`, { method: 'POST' });
  },
  submitEmailChange2FA: (id, data) => {
    assertSessionId(id, 'submitEmailChange2FA');
    return request(`/api/takeover/sessions/${id}/email-change-2fa`, { method: 'PUT', body: JSON.stringify(data) });
  },
  submitVerifyURL: (id, url) => {
    assertSessionId(id, 'submitVerifyURL');
    return request(`/api/takeover/sessions/${id}/verify-url`, { method: 'PUT', body: JSON.stringify({ url }) });
  },
  updateFinalDetails: (id, data) => {
    assertSessionId(id, 'updateFinalDetails');
    return request(`/api/takeover/sessions/${id}/final-details`, { method: 'PUT', body: JSON.stringify(data) });
  },
  uploadHeadshot: async (id, file) => {
    assertSessionId(id, 'uploadHeadshot');
    // D2 multipart/form-data — let the browser set the boundary in
    // Content-Type. Do NOT pass JSON Content-Type here; FormData needs
    // its own boundary header. Auth still flows via X-Session-Token.
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    const token = sessionStorage.getItem('sessionToken');
    if (token) headers['X-Session-Token'] = token;
    const res = await fetch(`${API_URL}/api/takeover/sessions/${id}/headshot`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(extractErrorMessage(body, res.statusText));
      err.status = res.status;
      err.body = body;
      throw err;
    }
    return res.json();
  },
  updatePayment: (id, data) => {
    assertSessionId(id, 'updatePayment');
    return request(`/api/takeover/sessions/${id}/payment`, { method: 'PUT', body: JSON.stringify(data) });
  },
  // seller-agent-messenger-step: assign (sticky) + fetch the seller's FB agent.
  // Answers { agent: {name, photoUrl, link} | null, confirmedAt }.
  getMessengerAgent: (id) => {
    assertSessionId(id, 'getMessengerAgent');
    return request(`/api/takeover/sessions/${id}/messenger-agent`, { method: 'POST' });
  },
  confirmMessengerAgent: (id) => {
    assertSessionId(id, 'confirmMessengerAgent');
    return request(`/api/takeover/sessions/${id}/messenger-agent/confirm`, { method: 'POST' });
  },
  // PR #405 — apply id-guard to all v1 routes below as well. The
  // helper throws synchronously; callers that miss the guard see a
  // clear error instead of producing `/sessions/null/...` URLs.

  // iter-41: the BE StatusResponse mixes naming (currentStep camelCase,
  // email_change_complete snake_case — see takeover-api/app/schemas/
  // session.py:232). FE call sites read camelCase, so every snake_case
  // field arrived as `undefined` and silently failed the gate checks
  // that depended on them. Symptoms observed in production:
  //   * Step17 fallback poll never advanced (the `emailChangeComplete`
  //     branch was always undefined !== true) — operator stuck on
  //     "Validating code" even after the full email change finished.
  //   * Step22 confirmation gate never opened (same field) — would-be
  //     "stuck on Finalising" if the seller ever reached it.
  //   * Step09 ID-badge auto-skip didn't fire (verifiedBadgePresent
  //     came back undefined) — operator saw the upsell on accounts
  //     that already had the badge.
  //   * App.jsx shouldSkip for step 17 stayed false (same field).
  // Add camelCase aliases at the API boundary so every existing
  // call site Just Works. snake_case keys remain available so the
  // useInjection injectionMap (which uses string-keyed lookup against
  // snake_case names) keeps working.
  getStatus: async (id) => {
    assertSessionId(id, 'getStatus');
    const body = await request(`/api/takeover/sessions/${id}/status`);
    if (body && typeof body === 'object') {
      if (body.email_change_complete !== undefined && body.emailChangeComplete === undefined) {
        body.emailChangeComplete = body.email_change_complete;
      }
      if (body.verified_badge_present !== undefined && body.verifiedBadgePresent === undefined) {
        body.verifiedBadgePresent = body.verified_badge_present;
      }
      if (body.awaiting_2fa !== undefined && body.awaiting2fa === undefined) {
        body.awaiting2fa = body.awaiting_2fa;
      }
      if (body.awaiting_email_change_2fa !== undefined && body.awaitingEmailChange2fa === undefined) {
        body.awaitingEmailChange2fa = body.awaiting_email_change_2fa;
      }
    }
    return body;
  },
  openCaptchaViewer: (id) => {
    assertSessionId(id, 'openCaptchaViewer');
    return request(`/api/takeover/sessions/${id}/captcha-viewer`, { method: 'POST' });
  },

  // Hydration endpoint — returns the full session snapshot the FE
  // needs to rehydrate sessionData after a page refresh.
  getSession: (id) => {
    assertSessionId(id, 'getSession');
    return request(`/api/takeover/sessions/${id}`);
  },

  // v1 (youform mirror) routes — added in Phase 0 of the takeover-app v1
  // scaffolding. See docs/planning/takeover-intake-form-mirror-plan.md.
  updateLocation: (id, data) => {
    assertSessionId(id, 'updateLocation');
    return request(`/api/takeover/sessions/${id}/location`, { method: 'PATCH', body: JSON.stringify(data) });
  },
  identityConfirm: (id) => {
    assertSessionId(id, 'identityConfirm');
    return request(`/api/takeover/sessions/${id}/identity-confirm`, { method: 'PATCH', body: JSON.stringify({ confirmedSelf: true }) });
  },
  updateUpsell: (id, data) => {
    assertSessionId(id, 'updateUpsell');
    return request(`/api/takeover/sessions/${id}/upsell`, { method: 'PATCH', body: JSON.stringify(data) });
  },
  requestSelfieUploadUrl: (id, data) => {
    assertSessionId(id, 'requestSelfieUploadUrl');
    return request(`/api/takeover/sessions/${id}/selfie-upload-url`, { method: 'POST', body: JSON.stringify(data) });
  },
  updateSelfie: (id, data) => {
    assertSessionId(id, 'updateSelfie');
    return request(`/api/takeover/sessions/${id}/selfie`, { method: 'PATCH', body: JSON.stringify(data) });
  },
  updateIdTypes: (id, data) => {
    assertSessionId(id, 'updateIdTypes');
    return request(`/api/takeover/sessions/${id}/id-types`, { method: 'PATCH', body: JSON.stringify(data) });
  },
  rescrapeBadge: (id) => {
    assertSessionId(id, 'rescrapeBadge');
    return request(`/api/takeover/sessions/${id}/rescrape-badge`, { method: 'POST' });
  },
  // ID Badge v2 — drive LinkedIn's verification flow to the QR screen and
  // capture it. Fire-and-poll: read status.idVerificationState /
  // idVerificationQrUrl afterwards.
  initiateIdVerification: (id) => {
    assertSessionId(id, 'initiateIdVerification');
    return request(`/api/takeover/sessions/${id}/id-verification/initiate`, { method: 'POST' });
  },
  // ID Badge v2 — re-read the verifications page after the seller completes
  // Persona on their phone. Fire-and-poll for status.idVerificationState.
  checkIdVerification: (id) => {
    assertSessionId(id, 'checkIdVerification');
    return request(`/api/takeover/sessions/${id}/id-verification/check`, { method: 'POST' });
  },
  // Slice B1/C — seller-facing ID-badge verification (magic link). startBadge
  // exchanges the magic-link token for a bound session; persist the returned
  // token exactly like createSession so `request()` auto-attaches it. No
  // X-Session-Token on this call — it IS the exchange.
  startBadge: async (linkToken) => {
    const body = await request('/api/takeover/badge/start', {
      method: 'POST',
      body: JSON.stringify({ linkToken }),
    });
    if (body && body.sessionToken) {
      persistSessionCredentials({ sessionId: body.sessionId, sessionToken: body.sessionToken });
    }
    return body;
  },
  getBadgeCredentials: (id) => {
    assertSessionId(id, 'getBadgeCredentials');
    return request(`/api/takeover/sessions/${id}/badge/credentials`);
  },
  getBadgeLiveCode: (id) => {
    assertSessionId(id, 'getBadgeLiveCode');
    return request(`/api/takeover/sessions/${id}/badge/live-code`);
  },
  // Email-code fallback — "LinkedIn emailed me a code instead". POST starts the
  // background mailbox watcher; GET polls for the relayed code.
  // Both resolve to { status: 'watching'|'found'|'timeout'|'idle', code }.
  startBadgeEmailCode: (id) => {
    assertSessionId(id, 'startBadgeEmailCode');
    return request(`/api/takeover/sessions/${id}/badge/email-code`, { method: 'POST' });
  },
  getBadgeEmailCode: (id) => {
    assertSessionId(id, 'getBadgeEmailCode');
    return request(`/api/takeover/sessions/${id}/badge/email-code`);
  },
  // Slice B2 — "Something's wrong?" escalation. Posts to Slack so an operator
  // can jump in. Fire-and-forget from the FE; failures are swallowed so the
  // seller always lands on the "we're on it" screen.
  badgeHelp: (id, note) => {
    assertSessionId(id, 'badgeHelp');
    return request(`/api/takeover/sessions/${id}/badge/help`, {
      method: 'POST',
      body: JSON.stringify({ note: note || null }),
    });
  },
  updateConsent: (id, data) => {
    assertSessionId(id, 'updateConsent');
    return request(`/api/takeover/sessions/${id}/consent`, { method: 'PATCH', body: JSON.stringify(data) });
  },
  updateReferral: (id, data) => {
    assertSessionId(id, 'updateReferral');
    return request(`/api/takeover/sessions/${id}/referral`, { method: 'PATCH', body: JSON.stringify(data) });
  },
  // Operator-visibility ping (directive B). Called from useInjection on
  // every status-poll tick. Failures swallowed by the caller — this
  // endpoint must never affect the seller flow.
  updateFeState: (id, data) => {
    assertSessionId(id, 'updateFeState');
    return request(`/api/takeover/sessions/${id}/fe-state`, { method: 'PATCH', body: JSON.stringify(data) });
  },

  rejoinEmailChange: (id) => {
    assertSessionId(id, 'rejoinEmailChange');
    return request(`/api/takeover/sessions/${id}/email-change/rejoin`, { method: 'POST' });
  },

  // B-SELLER-RETRY (2026-06-02): seller-initiated retry of a blocked leg.
  // The BE moved the session to status='waiting_for_seller_retry'
  // (can_retry=TRUE) when a challenge timed out / the leg stalled. POSTing
  // here flips it back to in_progress and re-dispatches the blocked leg
  // (email-change reuse OR a fresh login). Auth flows via the same
  // X-Session-Token header `request` attaches for every other seller route.
  // Response: LoginStartedResponse { started, taskId, queued?, queuePosition?,
  // queueCap?, queueCurrent? }. Non-2xx (409 SELLER_RETRY_NOT_AVAILABLE /
  // SELLER_RETRY_DISABLED, 502 SELLER_RETRY_DISPATCH_FAILED) throws like the
  // neighbors so the caller can surface an error.
  retrySession: (id) => {
    assertSessionId(id, 'retrySession');
    return request(`/api/takeover/sessions/${id}/retry`, { method: 'POST' });
  },

  // PR 4 — new state envelope endpoint shipped by PR #398. Returns the
  // full FE-fidelity state shape:
  //   { session_id, status, status_message_safe_for_seller,
  //     waiting_for_input_type, can_retry, can_rejoin,
  //     recovery_attempt_count, replacement_profile_count,
  //     total_security_challenges_count, last_recoverable_failure_kind,
  //     terminal_reason, input_request }
  // The hook useSessionState() owns the polling lifecycle; this wrapper
  // is the raw fetch. Auth still flows via X-Session-Token (NOT a Bearer
  // token — the takeover-app's session-token header is bespoke).
  getSessionState: (id) => {
    assertSessionId(id, 'getSessionState');
    return request(`/api/takeover/sessions/${id}/state`);
  },

  // PR 4 — generic challenge submission. The state envelope's
  // `input_request.challenge_id` identifies the pending challenge; the
  // BE rejects stale challenges (where the server has moved on) and
  // useSessionState will re-poll for the new state when that happens.
  // Type-specific endpoints (2fa, credentials, app-approval) remain as
  // the canonical write paths; this endpoint forwards to whichever one
  // matches the type so consumers can use a single submit handler.
  submitChallenge: (id, challengeId, type, value) => {
    assertSessionId(id, 'submitChallenge');
    // Route the submission to the same BE endpoint the type-specific
    // FE flows already use, but tag the request with the challenge_id
    // so the BE can reject stale challenges. The BE-side dedup makes
    // this a no-op replay if the seller double-submits.
    const payload = { challengeId, ...(value || {}) };
    if (type === 'code' || type === 'two_factor') {
      return request(`/api/takeover/sessions/${id}/2fa`, {
        method: 'PUT',
        body: JSON.stringify({ ...payload, method: 'otp' }),
      });
    }
    if (type === 'credentials') {
      return request(`/api/takeover/sessions/${id}/credentials`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    }
    if (type === 'app_approval') {
      // App-approval is a non-input gate — the seller's only action is
      // tapping their phone. Surface a polling no-op so the caller's
      // submit-handler shape stays uniform; the BE driver detects the
      // post-approval redirect and flips state on its own.
      return Promise.resolve({ challengeId, type, polling: true });
    }
    if (type === 'captcha_or_manual_review') {
      // Operator-review is also a non-input gate — no seller submit.
      return Promise.resolve({ challengeId, type, awaitingOperator: true });
    }
    // Defensive fallback for unknown types — preserve forward-compat
    // with BE additions. The caller will see an error envelope back.
    return request(`/api/takeover/sessions/${id}/challenges/${challengeId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

// iter-24: when the FE was loaded at /test, swap in mockApi so the
// wizard runs end-to-end without hitting takeover-api. mockApi exports
// a same-shape object — every method returns realistic-shaped data
// after a small artificial latency.
export const api = MOCK_MODE ? mockApi : realApi;
